import { DataTable, DataTablePagination, EmptyState, TooltipProvider } from '@portals-platform/ui';
import type { Table } from '@tanstack/react-table';
import { FileTextIcon } from 'lucide-react';
import { type CertificateRow, useCertificateSearch } from '../../data-access/index.js';
import { useCertificateListState } from '../../hooks/use-certificate-list-state.js';
import { certificateColumns } from './certificate-columns.js';
import { CertificateSummaryCards } from './certificate-summary-cards.js';
import { CertificateToolbar } from './certificate-toolbar.js';

export interface CertificatesTableProps {
  compact?: boolean;
}

export function CertificatesTable({ compact = false }: CertificatesTableProps = {}) {
  const {
    params,
    pagination,
    setPagination,
    sorting,
    setSorting,
    status,
    searchInput,
    isFiltered,
    changeStatus,
    changeSearch,
    reset,
  } = useCertificateListState({ pageSize: compact ? 10 : 20 });

  const { rows, totalElements, totalPages, countsByStatus, isLoading, isError, error } =
    useCertificateSearch(params);

  if (isError) {
    return (
      <EmptyState
        icon={<FileTextIcon />}
        title="Could not load certificates"
        description={error?.message ?? 'Please try again.'}
      />
    );
  }

  const renderToolbar = (tableInstance: Table<CertificateRow>) => (
    <CertificateToolbar
      table={tableInstance}
      searchInput={searchInput}
      onSearchChange={changeSearch}
      status={status}
      onStatusChange={changeStatus}
      isFiltered={isFiltered}
      onReset={reset}
      totalElements={totalElements}
    />
  );

  const renderFooter = (tableInstance: Table<CertificateRow>) => (
    <DataTablePagination table={tableInstance} totalRows={totalElements} />
  );

  const fullModeSlots = compact ? {} : { toolbar: renderToolbar, footer: renderFooter };

  const table = (
    <DataTable<CertificateRow, never>
      columns={certificateColumns}
      data={rows}
      isLoading={isLoading}
      getRowId={(row) => row.id}
      server={{
        pageCount: totalPages,
        pagination,
        onPaginationChange: setPagination,
        sorting,
        onSortingChange: setSorting,
      }}
      emptyState={
        <EmptyState
          className="border-0"
          icon={<FileTextIcon />}
          title="No certificates match these filters"
          description="Try clearing the status filter or widening your search."
        />
      }
      {...fullModeSlots}
    />
  );

  if (compact) {
    return <TooltipProvider>{table}</TooltipProvider>;
  }

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-6">
        <CertificateSummaryCards
          totalElements={totalElements}
          countsByStatus={countsByStatus}
          status={status}
          onSelectStatus={changeStatus}
        />
        {table}
      </div>
    </TooltipProvider>
  );
}
